import React, { useState } from "react";
import { submitLeadQuietly } from "../lib/crm";
import programfee from "../assets/programfee.webp";
import eligibilityPic from "../assets/eligibilityPic.webp";
import svyasaMPic from "../assets/svyasaMPic.webp";
import logo from "../assets/logo.webp";
import { useNavigate } from "react-router-dom";

// --- ADD: your Web App URL here (must be the /exec URL) ---
const GAS_WEB_APP_URL = "https://script.google.com/macros/s/AKfycbxd0Y7ktEqdW_3GHXdnA3ak9s89xvzq0ZvLX480zUzEhL2XihZUFv4O4jp8airPjDtDFQ/exec";

// Card component
function Card({ title, content }) {
  return (
    <div className="p-4 border border-gray-200 rounded-lg shadow-sm bg-white">
      <h4 className="font-semibold text-blue-600 mb-1">{title}</h4>
      <p className="text-sm text-gray-700">{content}</p>
    </div>
  );
}

// Identifies this form to the CRM: becomes the campaign name and the key its
// Lead Routing rule matches on.
const CRM_FORM_ID = "svyasa";

export default function SvyasaLP() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("MBA");

  // --- ADD: form state & handlers ---
  const [form, setForm] = useState({
    fullName: "",
    phone: "",
    email: "",
    city: "",
    state: "",
    course: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ ok: null, msg: "" });
  const [gotcha, setGotcha] = useState(""); // bot trap; kept out of the Sheet payload

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ ok: null, msg: "" });

    if (!form.fullName || !form.phone || !form.email || !form.course) {
      setStatus({ ok: false, msg: "Please fill all required fields." });
      return;
    }

    const cleanPhone = form.phone.replace(/[\s-]/g, "");

    const phoneRegex = /^[6-9][0-9]{9}$/;
    const allSameDigitRegex = /^(\d)\1{9}$/;
    const isSequential = cleanPhone === "9876543210" || cleanPhone === "9999888877" || cleanPhone === "9898989898";

    if (!phoneRegex.test(cleanPhone) || allSameDigitRegex.test(cleanPhone) || isSequential) {
      setStatus({ ok: false, msg: "Please enter a valid 10-digit phone number." });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      setStatus({ ok: false, msg: "Please enter a valid email address." });
      return;
    }

    try {
      setSubmitting(true);

      // Build query string from form state
      const qs = new URLSearchParams({ ...form, phone: cleanPhone, source: "SvyasaLP" }).toString();

      // Double-writing while the CRM proves itself: the Sheet is still the
      // source of truth, so only its outcome decides what the visitor sees.
      const [sheetResult] = await Promise.allSettled([
        fetch(GAS_WEB_APP_URL, {
          method: 'POST',
          mode: 'no-cors',
          body: qs,
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }),
        submitLeadQuietly({
          formId: CRM_FORM_ID,
          fields: { ...form, phone: cleanPhone, _gotcha: gotcha },
        }),
      ]);
      if (sheetResult.status === "rejected") throw sheetResult.reason;
      const response = sheetResult.value;

      let result;
      try {
        result = await response.json();
      } catch (e) {
        // Fallback if GAS doesn't return proper JSON or no-cors is acting up
        result = { status: "success" };
      }

      if (result.status === "success") {
        setStatus({ ok: true, msg: "Thanks! We’ll contact you shortly." });
        setForm({ fullName: "", phone: "", email: "", city: "", state: "", course: "" });
      } else {
        setStatus({ ok: false, msg: "Something went wrong." });
      }
    } catch {
      setStatus({ ok: false, msg: "Network error. Please try again." });
    } finally {
      setSubmitting(false);
    }
  }
  const renderProgramDetails = () => {
    switch (activeTab) {
      case "MBA":
        return (
          <section id="mba-overview" className="mt-8">
            <div className="mb-6">
              <h2 className="text-3xl font-extrabold text-gray-900 mb-3">MBA PRO in AI and Data Analytics</h2>
              <span className="px-4 py-1.5 bg-blue-100 text-blue-800 rounded-full text-sm font-bold tracking-wide shadow-sm mb-4 inline-block">
                Powered by SAS
              </span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              {/* Text content */}
              <div className="lg:col-span-2 text-gray-700 leading-relaxed space-y-4 text-justify text-lg pt-2">
                <p className="mb-4">
                  The MBA PRO in AI and Data Analytics is a two-year postgraduate management program offered by S-VYASA University.
                  This course enables students to master AI, decode data, and drive business decisions with engineering precision.
                </p>

                <p className="mb-4">
                  Unlike conventional MBA programs, our curriculum goes beyond basic coding and data analysis—it is designed to engineer
                  the entire decision-making pipeline, from data ingestion to insight generation and intelligent action. Students develop
                  a Decision Engineering mindset, building critical thinking from first principles and learning how to transform ideas
                  into deployable, data-driven solutions.
                </p>

                <p className="mb-4">
                  The program features SAS-powered full-stack skills in data modeling, risk analytics, and visualization, combined with
                  hands-on training in scalable, cloud-native platforms. With a strong emphasis on ethical and impactful AI, students
                  explore responsible AI practices, embedded storytelling, and business ethics—preparing them to lead with insight,
                  integrity, and innovation in today's data-driven world.
                </p>
              </div>

              {/* --- UPDATED: counselor form wired up --- */}
              <div className="bg-white p-6 rounded-2xl shadow-xl border border-gray-100 h-fit sticky  mb-10">
                <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">Register Your Interest</h3>
                <form onSubmit={onSubmit} className="flex flex-col gap-4 ">
                  {/* Bot trap: hidden from people, so anything typed here is spam. */}
                  <input
                    type="text"
                    name="_gotcha"
                    value={gotcha}
                    onChange={(e) => setGotcha(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                  />
                  <input
                    type="text"
                    name="fullName"
                    placeholder="Full Name *"
                    value={form.fullName}
                    onChange={onChange}
                    className="p-3 border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number (10 digits) *"
                    value={form.phone}
                    onChange={onChange}
                    maxLength="10"
                    pattern="[0-9]{10}"
                    className="p-3 border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address *"
                    value={form.email}
                    onChange={onChange}
                    className="p-3 border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="city"
                      placeholder="City"
                      value={form.city}
                      onChange={onChange}
                      className="p-3 border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="text"
                      name="state"
                      placeholder="State"
                      value={form.state}
                      onChange={onChange}
                      className="p-3 border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <select
                    name="course"
                    value={form.course}
                    onChange={onChange}
                    className="p-3 border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    required
                  >
                    <option value="" disabled>Select Course *</option>
                    <option value="MBA PRO in AI and Data Analytics">MBA PRO in AI and Data Analytics</option>
                    <option value="MCA">MCA</option>
                    <option value="MSc">MSc</option>
                  </select>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-bold py-3 px-4 rounded-xl shadow-md hover:from-blue-600 hover:to-indigo-700 transition-all disabled:opacity-60"
                  >
                    {submitting ? "Submitting..." : "Submit Details"}
                  </button>

                  {status.msg && (
                    <p className={`text-sm mt-2 font-medium ${status.ok ? "text-green-600" : "text-red-500"}`}>
                      {status.msg}
                    </p>
                  )}
                </form>
              </div>
            </div>

            <div className="w-full h-[2px] bg-black"></div>


            {/* Eligibility */}
            {/* Eligibility + image beside it */}
            <div className="flex flex-col lg:flex-row items-stretch gap-8 mb-8 mt-12">
              {/* Orange Eligibility box */}
              <div className="bg-gradient-to-br from-orange-400 to-orange-500 text-white p-8 rounded-2xl shadow-lg flex-1">
                <h3 className="text-2xl font-bold mb-4 border-b border-orange-300 pb-2">Eligibility</h3>
                <ul className="list-disc pl-5 space-y-3 font-medium text-orange-50 text-[15px] leading-snug">
                  <li>A bachelor’s degree with 50% from any recognized university.</li>
                  <li>Completed higher secondary education (10+2) or equivalent with a minimum aggregate score of 50%.</li>
                  <li>Optional: Valid cut-off score on relevant entrance exams (CAT, MAT, XAT, PG-CET).</li>
                  <li>Note: 5% relaxation for SC/ST/OBC students for eligibility of admission.</li>
                  <li>Age Limit: No strict limit, but candidates with a 3+ year career/degree gap may require a bridge programme.</li>
                  <li>Work Experience: Optional. No specific requirement of relevant work experience in the field.</li>
                </ul>
              </div>

              {/* Image outside the orange box */}
              <div className="flex-1 flex justify-center items-center">
                <img
                  src={eligibilityPic}
                  alt="Eligibility"
                  className="rounded-2xl shadow-xl w-full h-full object-cover min-h-[320px] max-h-[420px]"
                />
              </div>
            </div>


            {/* Program Highlights */}
            {/* Program Highlights */}
            <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-8 rounded-3xl shadow-sm mt-12 border border-orange-200">
              <h3 className="text-2xl mb-6 font-bold text-gray-800 text-center">Program Highlights</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card title="Comprehensive Curriculum" content="A robust theoretical foundation in data science and decision engineering, reinforced with practical, hands-on training." />
                <Card title="Decision Engineering Mindset" content="Develop critical thinking from first principles and learn to design deployable, data-driven business solutions." />
                <Card title="SAS-Powered Full-Stack Skills" content="Gain mastery in data modeling, risk analytics, and data visualization using industry-leading SAS technology." />
                <Card title="Cloud-Native Learning" content="Hands-on experience with scalable, cloud-based tools used in modern enterprise environments." />
                <Card title="Career-Focused Certifications" content="Graduate with recognized certifications from leading organizations to enhance your portfolio and global employability." />
                <Card title="End-to-End Technology Stack" content="Progress from fundamentals to advanced topics, mastering the tools and techniques that power today’s AI and analytics systems." />
                <Card title="Real Projects, Real Results" content="Work on real-world projects that solve practical business challenges and demonstrate your job readiness." />
                <Card title="Ethical & Impactful AI" content="Learn responsible AI practices, embedded storytelling, and business ethics for ethical leadership in tech." />
              </div>
            </div>

            {/* Program Fee */}
            <div className="mt-16 text-center">
              <h3 className="text-2xl font-bold text-gray-800 mb-6 inline-block border-b-2 border-orange-300 pb-2">Program Fee Structure</h3>
              <div className="flex justify-center mt-2">
                <img src={programfee} alt="MBA Program Fees" className="rounded-2xl shadow-xl w-full max-w-4xl border border-gray-100" />
              </div>
            </div>

            {/* Career Outcome */}
            <div className="mt-16">
              <h3 className="text-2xl font-bold text-gray-800 mb-6 border-b-2 border-orange-300 pb-2 inline-block">Career Outcomes</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
                <Card title="Data Analyst" content="Transform raw data into actionable insights using statistical tools and visualizations to drive smarter decisions." />
                <Card title="Machine Learning Engineer" content="Design and deploy scalable machine learning models that automate and enhance systems." />
                <Card title="NLP Specialist" content="Develop intelligent systems that interpret human language for chatbots, sentiment analysis, and translation." />
                <Card title="Decision Intelligence Consultant" content="Guide strategic choices through predictive modeling, scenario planning, and insight-driven recommendations." />
                <Card title="Business Analyst" content="Bridge the gap between business needs and solutions by analyzing processes and recommending improvements." />
                <Card title="Risk Analyst" content="Analyze trends and develop strategies to mitigate operational and financial risks." />
                <Card title="Forecasting Strategist" content="Predict trends using statistical models to help businesses plan and stay ahead." />
                <Card title="Credit AI Architect" content="Design systems that assess creditworthiness and automate fair and efficient lending decisions." />
                <Card title="AI Product Manager" content="Lead AI product development by aligning with user needs, business goals, and innovation." />
                <Card title="AI Strategist" content="Integrate AI solutions to enhance performance and drive innovation in organizations." />
              </div>
            </div>

            {/* Course Structure */}

            {/* Course Structure */}
            <div className="mt-16 mb-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-6 border-b-2 border-orange-300 pb-2 inline-block">Course Structure</h3>
              <div className="grid grid-cols-1 gap-6 mt-2">
                <div className="p-4 border rounded-2xl shadow-md bg-white">
                  <h2 className="font-bold text-lg mb-3">
                    Semester 1: Foundations of Data, Code & Thinking
                  </h2>
                  <ul className="list-disc pl-5 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                    {`Management Fundamentals
Managerial Economics
Organizational Behaviour
Business Accounting
Business Statistics
Basic Analytics Programming
Foundations of AI (IBM)
Essence of Yoga: Principles and Practical Applications
Mini Bootcamp: Python for Analytics + SQL + Git
Workshop: Introduction to Decision Intelligence + AI Canvas`
                      .split("\n")
                      .map((item, index) => (
                        <li key={index} className="text-gray-700">{item}</li>
                      ))}
                  </ul>
                </div>
                <div className="p-4 border rounded-2xl shadow-md bg-white">
                  <h2 className="font-bold text-lg mb-3">
                    Semester 2: Analytics, Modeling & Intelligence
                  </h2>
                  <ul className="list-disc pl-5 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                    {`Legal Aspects of Business
Programming with R & Python
Marketing Management
Financial Management for Business
International Business Environment & Management
Big Data Visualization BASICS
Marketing & Financial Management (with ML mini-projects)
PowerBI and Tableau (Microsoft certification)
Exploring Yogic Philosophy, Happiness & Human Values`
                      .split("\n")
                      .map((item, index) => (
                        <li key={index} className="text-gray-700">{item}</li>
                      ))}
                  </ul>
                </div>
                <div className="p-4 border rounded-2xl shadow-md bg-white">
                  <h2 className="font-bold text-lg mb-3">
                    Semester 3: AI Modeling & Visualization
                  </h2>
                  <ul className="list-disc pl-5 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                    {`Cloud Computing and Infrastructure Management (AWS)
Big Data Visualization – Advanced
Design of AI products and services
AI-driven marketing for growth
Machine learning
Indian Ethos & Leadership
Summer Internship
Project: Build a credit risk or pricing optimization model`
                      .split("\n")
                      .map((item, index) => (
                        <li key={index} className="text-gray-700">{item}</li>
                      ))}
                  </ul>
                </div>
                <div className="p-4 border rounded-2xl shadow-md bg-white">
                  <h2 className="font-bold text-lg mb-3">
                    Semester 4: Applied AI + Decision Engineering & Risk
                  </h2>
                  <ul className="list-disc pl-5 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                    {`AI product management
Financial Modelling for Technology Managers
Project and operations management for digital transformation
Business Risk with SAS
Data Strategy, Governance, and Business Intelligence (IBM Certification)`
                      .split("\n")
                      .map((item, index) => (
                        <li key={index} className="text-gray-700">{item}</li>
                      ))}
                  </ul>
                </div>

              </div>
            </div>

          </section>
        );


      case "MCA":
        return <p className="mt-4">Details for MCA program coming soon...</p>;
      case "MSc":
        return <p className="mt-4">Details for MSc program coming soon...</p>;
      default:
        return null;
    }
  };

  return (
    <div className="p-4 max-w-6xl mx-auto">
      {/* Logo section intentionally hidden */}
      {false && (
        <div className="bg-white p-4 rounded-xl shadow-md flex items-center gap-4 mb-4">
          <img src={logo} alt="S-VYASA Logo" className="h-14" />
        </div>
      )}

      {/* Banner */}
      <div className="relative">
        <img src={svyasaMPic} alt="S-VYASA Hero Banner" className="w-full rounded-xl shadow-lg" />
      </div>

      {/* Tabs (also fixed template string bug) */}
      {false && (
        <div className="flex gap-4 mt-6 justify-start">
          <button
            onClick={() => setActiveTab("MBA")}
            className={`px-6 py-2 rounded-full font-medium transition-all duration-300
    ${activeTab === "MBA"
                ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg scale-105"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300 shadow-sm hover:scale-105"}`}
          >
            MBA
          </button>

          <button
            onClick={() => setActiveTab("MCA")}
            className={`px-6 py-2 rounded-full font-medium transition-all duration-300
    ${activeTab === "MCA"
                ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg scale-105"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300 shadow-sm hover:scale-105"}`}
          >
            MCA
          </button>

          <button
            onClick={() => setActiveTab("MSc")}
            className={`px-6 py-2 rounded-full font-medium transition-all duration-300
    ${activeTab === "MSc"
                ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg scale-105"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300 shadow-sm hover:scale-105"}`}
          >
            MSc
          </button>

        </div>
      )}

      {false && (
        <div className="flex gap-4 mt-6 justify-end">
          <button
            onClick={() => navigate("/contact")}
            className="px-6 py-2 mr-2 rounded-full text-white 
             bg-gradient-to-r from-orange-500 to-orange-600 
             shadow-lg hover:from-orange-600 hover:to-orange-700
             hover:shadow-xl transform hover:scale-105 transition-all duration-300"
          >
            Contact Us
          </button>

          <button
            onClick={() => {
              console.log("User clicked Call Now");
              window.location.href = "tel:+919900911649";
            }}
            className="px-6 py-2 mr-4 rounded-full text-white 
             bg-gradient-to-r from-green-500 to-green-600
             shadow-lg hover:from-green-600 hover:to-green-700
             hover:shadow-xl transform hover:scale-105 transition-all duration-300"
          >
            📞 Call Now
          </button>


        </div>
      )}

      {renderProgramDetails()}

      <footer className="text-center text-sm text-gray-500 mt-10 border-t pt-6">
        ©️ 2025 S-VYASA University · All rights reserved
      </footer>
    </div>
  );
}
